import { useMemo, useState } from "react";

import { dayLabel } from "../domain/date";
import { shortFoodName } from "../domain/foodView";
import { totalsOf } from "../domain/intake";
import {
  fitPanel,
  searchView,
  statsFor,
  swapOptions,
  verdictFor,
  type SwapFrame,
  type Verdict,
} from "../domain/planFit";
import { candidatesOf, fractionText, servingText } from "../domain/planner";
import type { FoodEntry, FoodItem } from "../domain/types";
import { searchFoods } from "../lib/foodDb";
import { useKaya } from "../store/kayaStore";
import { removePlanItem, replacePlanItems } from "../store/toggles";
import { Button } from "./Button";
import { CustomFoodSheet } from "./CustomFoodSheet";
import { I } from "./icons";
import { NavRow, RowGroup } from "./Rows";
import { Sheet } from "./Sheet";
import "./Plan.css";

const n = (v: number) => Math.round(v).toLocaleString();

type Mode = { kind: "options" } | { kind: "search" } | { kind: "fit"; v: Verdict };

/**
 * Swap one planned food (FoodSwap → FoodSwapSearch → FoodSwapFit; ADR-043, kit §34–35).
 *
 * First three like-for-like options, each with its reason; then "Something else", a search of the
 * whole table where every row carries its verdict against the meal and the day as they would stand
 * after the swap. A row that fits swaps at once; one that does not opens the fit panel, which names
 * each figure and offers the largest household portion that fits beside "Swap anyway". Every swap
 * goes through `replacePlanItems` — a tombstone and a new id.
 */
export function SwapSheet({
  frame,
  foods,
  lookup,
  today,
  onClose,
}: {
  frame: SwapFrame;
  /** the table plus the person's own foods */
  foods: FoodItem[];
  lookup: (code: string) => FoodItem | null;
  today: string;
  onClose: () => void;
}) {
  const { S, update, showToast } = useKaya();
  const [mode, setMode] = useState<Mode>({ kind: "options" });
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(false);
  const f = frame;
  const old = f.old;
  const mealCap = f.mealWord.charAt(0).toUpperCase() + f.mealWord.slice(1);
  const oldName = shortFoodName(old.name).toLowerCase();

  const opts = useMemo(() => swapOptions(S, f, candidatesOf(S, foods, lookup), lookup, today), [S, f, foods, lookup, today]);
  const stats = useMemo(() => statsFor(S), [S]);
  const results = useMemo(() => {
    const hits = q.trim().length >= 2 ? searchFoods(q, 40, foods) : [];
    return searchView(f, hits.filter((x) => x.i !== old.code).map((x) => verdictFor(S, f, x, stats)), q);
  }, [q, foods, f, S, stats, old.code]);

  const swapTo = (e: FoodEntry, words: string) => {
    update((d) => replacePlanItems(d, f.ds, [old.id], [{ ...e, meal: f.meal }]));
    showToast(words);
    onClose();
  };

  const mealNow = totalsOf(f.day.filter((e) => e.meal === f.meal));

  if (mode.kind === "fit") {
    const v = mode.v;
    const p = fitPanel(f, v, lookup);
    if (!p || !v.entry) return null;
    const e = v.entry;
    return (
      <Sheet
        title={`Swap to ${v.item.n}?`}
        sub={`${mealCap} · ${dayLabel(f.ds)} · ${fractionText(e.qty, e.unit)} · ${n(e.kcal)} kcal · ${Math.round(e.p)} g protein`}
        onClose={onClose}
      >
        <section className="k-fit" aria-labelledby="k-fit-verdict">
          <p id="k-fit-verdict" className={`k-fit__verdict k-tone--${p.tone}`}>
            {p.verdict}
          </p>
          <p className="k-fit__sentence">{p.sentence}</p>
          <div className="k-rows">
            {p.lines.map((l) => (
              <div className="k-fit__line" key={l.label}>
                <p className={`k-fit__label k-tone--${l.tone}`}>{l.label}</p>
                <p className="k-fit__text">{l.text}</p>
              </div>
            ))}
          </div>
          <div className="k-fit__acts">
            {p.best ? (
              <Button size="lg" onClick={() => p.best && swapTo(p.best.entry, `${v.item.n} · ${n(p.best.entry.kcal)} kcal`)}>
                {p.best.label}
              </Button>
            ) : null}
            <Button variant="ghost" size="lg" onClick={() => swapTo(e, `${v.item.n} · ${n(e.kcal)} kcal`)}>
              {p.anyway}
            </Button>
            <Button variant="text" onClick={onClose}>
              {p.keep}
            </Button>
          </div>
          <p className="k-food-note">{p.note}</p>
        </section>
      </Sheet>
    );
  }

  if (mode.kind === "search") {
    const row = (v: Verdict) => {
      const e = v.entry;
      const sub = `${v.portion} · ${e ? `${n(e.kcal)} kcal · ${Math.round(e.p)} g protein` : "–"}`;
      const label = `Swap to ${v.item.n}, ${v.portion}, ${e ? `${n(e.kcal)} kcal, ${Math.round(e.p)} g protein` : ""}. ${v.text}`;
      const out = v.kind === "out";
      return (
        <button
          type="button"
          key={v.item.i || v.item.n}
          className="k-row k-swap-opt"
          aria-label={label}
          aria-disabled={out || undefined}
          onClick={() => {
            if (out || !e) return;
            if (v.kind === "fits") swapTo(e, `${v.item.n} · ${n(e.kcal)} kcal`);
            else setMode({ kind: "fit", v });
          }}
        >
          <span className="k-swap-opt__txt">
            <span className="k-swap-opt__name">{v.item.n}</span>
            <span className="k-swap-opt__sub">{sub}</span>
            <span className={`k-swap-opt__why k-verdict k-verdict--${v.kind}`}>{v.text}</span>
          </span>
          {out ? null : (
            <span className="k-row__end" aria-hidden="true">
              {I["chevron-right"]}
            </span>
          )}
        </button>
      );
    };
    return (
      <Sheet
        title={`Swap ${oldName}`}
        sub={`${mealCap} · ${dayLabel(f.ds)} · ${n(mealNow.kcal)} of ~${n(f.share)} kcal · ${Math.round(mealNow.p)} g protein`}
        onClose={onClose}
        footer={
          <div className="k-plan-sheetfoot">
            <Button variant="ghost" onClick={() => setMode({ kind: "options" })}>
              Back to the three options
            </Button>
          </div>
        }
      >
        <div className="k-swap-field">
          <label className="k-food-label" htmlFor="k-swap-q">
            Search {foods.length.toLocaleString()} foods
          </label>
          <div className="k-swap-field__box">
            {I.search}
            <input
              id="k-swap-q"
              type="search"
              value={q}
              placeholder="dal, roti, chai, paneer"
              onChange={(e) => {
                setQ(e.target.value);
                setOpen(false);
              }}
            />
          </div>
        </div>
        <p className="k-swap-role">
          {I.target}
          Anything fits that keeps {f.mealWord} near ~{n(f.share)} kcal{f.meal !== "snack" ? ` and ${f.T.mealFloor} g+ protein` : ""}
        </p>
        {q.trim().length >= 2 ? (
          <div className="k-rows">
            {results.shown.map(row)}
            {results.folded.length ? (
              open ? (
                results.folded.map(row)
              ) : (
                <button type="button" className="k-row k-swap-opt" aria-expanded={false} onClick={() => setOpen(true)}>
                  <span className="k-swap-opt__txt">
                    <span className="k-swap-opt__name">{results.foldLabel}</span>
                    <span className="k-swap-opt__sub">{results.foldSub}</span>
                  </span>
                  <span className="k-row__end" aria-hidden="true">
                    {I["chevron-down"]}
                  </span>
                </button>
              )
            ) : null}
            {results.out.map(row)}
            {!results.shown.length && !results.folded.length && !results.out.length ? (
              <div className="k-row k-food-empty">
                <span className="k-food-empty__txt">Nothing matches “{q.trim()}”</span>
              </div>
            ) : null}
          </div>
        ) : null}
        <RowGroup>
          <NavRow icon={I.plus} title="Not here? Add a custom food" subtitle="Name, serving and label values" onClick={() => setCustom(true)} />
        </RowGroup>
        <p className="k-food-note">Sorted by fit. A dish you have logged shows your usual portion; the rest show one serving.</p>
        <CustomFoodSheet open={custom} onClose={() => setCustom(false)} prefillName={q.trim()} onSaved={() => setCustom(false)} />
      </Sheet>
    );
  }

  return (
    <Sheet
      title={`Swap ${oldName}`}
      sub={`${mealCap} · ${dayLabel(f.ds)} · ${servingText(old.qty, old.unit)} · ${n(old.kcal)} kcal · ${Math.round(old.p)} g protein`}
      onClose={onClose}
      footer={
        <div className="k-plan-sheetfoot">
          <Button
            variant="danger"
            ariaLabel={`Remove ${oldName} from the plan`}
            onClick={() => {
              update((d) => removePlanItem(d, f.ds, old.id));
              showToast(`Removed ${old.name}`);
              onClose();
            }}
          >
            Remove it
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Keep it
          </Button>
        </div>
      }
    >
      <p className="k-swap-role">
        {I.target}
        {opts.roleLine}
      </p>
      {opts.options.length ? (
        <div className="k-rows">
          {opts.options.map((o) => (
            <button
              type="button"
              key={`${o.entry.code ?? ""}${o.entry.name}`}
              className="k-row k-swap-opt"
              aria-label={`Swap to ${o.entry.name}, ${o.portion}, ${n(o.entry.kcal)} kcal, ${Math.round(o.entry.p)} g protein`}
              onClick={() => swapTo(o.entry, `${o.entry.name} · ${n(o.entry.kcal)} kcal`)}
            >
              <span className="k-swap-opt__txt">
                <span className="k-swap-opt__name">{o.entry.name}</span>
                <span className="k-swap-opt__sub">
                  {o.portion} · {n(o.entry.kcal)} kcal · {Math.round(o.entry.p)} g protein
                </span>
                <span className="k-swap-opt__why">{o.reason}</span>
              </span>
              <span className="k-row__end" aria-hidden="true">
                {I["chevron-right"]}
              </span>
            </button>
          ))}
        </div>
      ) : (
        <p className="k-food-note">Nothing you eat fills this role within a quarter of its kcal — search all foods below.</p>
      )}
      <RowGroup ariaLabel="Search">
        <NavRow
          icon={I.search}
          title="Something else — search all foods"
          subtitle={`Kaya says whether it fits ${f.mealWord}`}
          onClick={() => setMode({ kind: "search" })}
        />
      </RowGroup>
    </Sheet>
  );
}
